/**
 * Datos sembrados de roles y alcances para el modo vista previa.
 *
 * Tiene estado, como el chatter: asignar un rol tiene que verse en la tabla
 * después de guardar.
 */
import { REGIONES, SOCIEDADES, UNIDADES } from './preview-maestros';

/* Los maestros salen de preview-maestros.ts: regiones y unidades de negocio
   llegan de ARMUS, no se configuran acá, y escribirlas de nuevo en este
   archivo era pedir que las dos listas se contradijeran. */
const DIMENSIONES = {
  company: SOCIEDADES,
  business_unit: UNIDADES,
  region: REGIONES
};

const CATALOGO = [
  {
    id: 1,
    code: 'gestor_proyecto',
    name: 'Gestor de Proyecto',
    description: 'Ve y edita únicamente los proyectos en los que figura como responsable.',
    scoped: true
  },
  {
    id: 2,
    code: 'mercado',
    name: 'Mercado',
    description: 'Ve los proyectos de su alcance. Solo lectura.',
    scoped: true
  },
  {
    id: 3,
    code: 'responsable_mercado',
    name: 'Responsable de Mercado',
    description: 'Ve y edita los proyectos de su alcance.',
    scoped: true
  },
  {
    id: 4,
    code: 'direccion_general',
    name: 'Dirección General',
    description: 'Ve todos los proyectos que estén dentro de su alcance.',
    scoped: true
  },
  {
    id: 5,
    code: 'control_gestion',
    name: 'Control de Gestión',
    description: 'Ve los proyectos de su alcance y el Monitor de carga, donde puede reprocesar.',
    scoped: true
  },
  {
    id: 6,
    code: 'administrador',
    name: 'Administrador',
    description: 'Configuración del sistema y Monitor de carga, con reproceso. No ve el Dashboard.',
    scoped: false
  },
  {
    id: 7,
    code: 'administrador_usuarios',
    name: 'Administrador de Usuarios',
    description: 'Asigna roles y alcances. No ve el Dashboard.',
    scoped: false
  }
];

interface RolAsignadoDemo {
  code: string;
  nombre: string;
  acotado: boolean;
  todas_sociedades: boolean;
  todas_unidades: boolean;
  todas_regiones: boolean;
  sociedades: number[];
  unidades: number[];
  regiones: number[];
}

const USUARIOS: { id: number; nombre: string; correo: string; roles: RolAsignadoDemo[] }[] = [
  {
    id: 1,
    nombre: 'Facundo Aguirre',
    correo: 'faguirre@vippinn.com',
    roles: [
      {
        code: 'direccion_general',
        nombre: 'Dirección General',
        acotado: true,
        todas_sociedades: true,
        todas_unidades: true,
        todas_regiones: true,
        sociedades: [],
        unidades: [],
        regiones: []
      }
    ]
  },
  {
    id: 2,
    nombre: 'Silvina Álvarez',
    correo: 'salvarez@vippinn.com',
    roles: [
      {
        code: 'responsable_mercado',
        nombre: 'Responsable de Mercado',
        acotado: true,
        todas_sociedades: false,
        todas_unidades: true,
        todas_regiones: true,
        sociedades: [1],
        unidades: [],
        regiones: []
      }
    ]
  },
  { id: 3, nombre: 'Juan Pérez', correo: 'jperez@vippinn.com', roles: [] },
  { id: 4, nombre: 'Elena Ruiz', correo: 'eruiz@vippinn.com', roles: [] },
  {
    id: 5,
    nombre: 'Martín Sosa',
    correo: 'msosa@vippinn.com',
    roles: [
      {
        code: 'control_gestion',
        nombre: 'Control de Gestión',
        acotado: true,
        todas_sociedades: true,
        todas_unidades: true,
        todas_regiones: true,
        sociedades: [],
        unidades: [],
        regiones: []
      }
    ]
  },
  { id: 6, nombre: 'Paula Giménez', correo: 'pgimenez@vippinn.com', roles: [] }
];

/** Resuelve /api/me y /api/admin/*. Null si la url no es de las suyas. */
export function respuestaRoles(
  metodo: string,
  ruta: string,
  params: URLSearchParams,
  cuerpo: unknown
): unknown | null {
  if (/\/api\/me$/.test(ruta)) {
    return {
      success: true,
      data: {
        id: 1,
        nombre: 'Facundo Aguirre',
        correo: 'faguirre@vippinn.com',
        roles: ['direccion_general', 'administrador_usuarios'],
        /* Todos: la vista previa sirve para recorrer todas las pantallas, y
           desde que las rutas piden permiso (26/10/2026) sin estos se redirigía. */
        permisos: ['ver_dashboard', 'editar_proyecto', 'ver_monitor', 'reprocesar', 'ver_configuracion', 'cerrar_periodo', 'administrar_usuarios', 'ver_reporte_salud'],
        /* Falso a propósito: en la vista previa se recorren todas las
           pantallas. Poniéndolo en true el menú se filtra de verdad y sirve
           para probar la matriz. */
        roles_activos: false
      }
    };
  }

  if (/\/api\/admin\/roles$/.test(ruta)) {
    return { success: true, data: CATALOGO };
  }

  if (/\/api\/admin\/dimensiones$/.test(ruta)) {
    return { success: true, data: DIMENSIONES };
  }

  const guardar = ruta.match(/\/api\/admin\/usuarios\/(\d+)\/roles$/);
  if (guardar && metodo === 'PUT') {
    const usuario = USUARIOS.find((u) => u.id === Number(guardar[1]));
    if (!usuario) return { success: false, message: 'Usuario no encontrado' };

    const pedidos = (cuerpo as { roles: RolAsignadoDemo[] }).roles ?? [];

    usuario.roles = pedidos.map((p) => {
      const rol = CATALOGO.find((c) => c.code === p.code);

      return {
        code: p.code,
        nombre: rol?.name ?? p.code,
        acotado: rol?.scoped ?? true,
        // Un rol sin alcance queda abierto, igual que en el backend
        todas_sociedades: !rol?.scoped || p.todas_sociedades,
        todas_unidades: !rol?.scoped || p.todas_unidades,
        todas_regiones: !rol?.scoped || p.todas_regiones,
        sociedades: p.sociedades ?? [],
        unidades: p.unidades ?? [],
        regiones: p.regiones ?? []
      };
    });

    return { success: true, data: usuario };
  }

  if (/\/api\/admin\/usuarios$/.test(ruta)) {
    const buscado = (params.get('buscar') ?? '').toLowerCase();
    // Por defecto solo los que tienen rol, igual que el backend
    const conRol = (params.get('con_rol') ?? '1') === '1';

    const lista = USUARIOS.filter(
      (u) =>
        (!buscado ||
          u.nombre.toLowerCase().includes(buscado) ||
          u.correo.toLowerCase().includes(buscado)) &&
        (!conRol || u.roles.length > 0)
    );

    return {
      success: true,
      data: lista,
      meta: { pagina: 1, paginas: 1, total: lista.length }
    };
  }

  return null;
}
